import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  AutomaticProxyCountry,
  BrowserProfileProxyType,
  ProxySettingsBody,
} from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Row } from '@tanstack/react-table'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import type { QueryClient, UseMutationResult } from 'react-query'
import { useQueryClient } from 'react-query'
import * as z from 'zod'

import OfIcon from '@/assets/icons/icon-of.svg'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { loadCreatorsFx } from '@/models/creators.ts'
import { useCreatorProxySettings } from '@/modules/creators/hooks/useCreatorProxySettings.ts'
import { checkProxy, sendStartSession } from '@/modules/desktop/utils.ts'
import { handleError } from '@/utils/error'

import type { Creator } from '../../types'

interface ProxyConfigurationDialogProps {
  showProxyConfigurationDialog: boolean
  setShowProxyConfigurationDialog: Dispatch<SetStateAction<boolean>>
  row: Row<Creator>
  firstLaunch: boolean
}

export type ProxyConfigurationFormValues = z.infer<
  typeof ProxyConfigurationFormSchema
>

const availableProxyTypes = ['NONE', 'AUTOMATIC', 'HTTP'] as const

const ProxyConfigurationFormSchema = z.object({
  proxyType: z.nativeEnum(BrowserProfileProxyType),
  host: z.string(),
  port: z.string(),
  username: z.string(),
  password: z.string(),
  automaticProxyCountry: z.nativeEnum(AutomaticProxyCountry).nullish(),
})

const onSubmit = (
  creator: Creator,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateMutation: UseMutationResult<any, unknown, ProxySettingsBody, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toast: any,
  queryClient: QueryClient,
  firstLaunch: boolean,
  proxyType: string,
  proxyCheckState: string,
  setProxyCheckState: Dispatch<
    SetStateAction<'NONE' | 'CHECKING' | 'SUCCESS' | 'ERROR'>
  >,
  setProxyError: (value: string) => void,
  setShowProxyConfigurationDialog: Dispatch<SetStateAction<boolean>>,
  values: ProxyConfigurationFormValues,
) => {
  if (proxyType === 'HTTP' && proxyCheckState !== 'SUCCESS') {
    // Needs proxy checking
    setProxyCheckState('CHECKING')
    checkProxy(
      values.host + ':' + values.port,
      values.username,
      values.password,
    ).then((result) => {
      if (result.success) {
        setProxyCheckState('SUCCESS')
      } else {
        setProxyError(result.error!)
        setProxyCheckState('ERROR')
      }
    })

    return
  }

  updateMutation
    .mutateAsync({
      proxyType: values.proxyType,
      host: values.host + ':' + values.port,
      username: values.username,
      password: values.password,
      country: values.automaticProxyCountry ?? undefined,
    })
    .then((response) => {
      toast.success('Successfully updated proxy settings.')
      setShowProxyConfigurationDialog(false)

      loadCreatorsFx()
      queryClient.invalidateQueries('creator')

      if (firstLaunch) {
        sendStartSession(creator)
      }
    })
    .catch((error) => {
      handleError(
        `Error while updating proxy settings. ${
          error.response ? (error.response.status ?? 0) : ''
        }`,
      )
    })
}

export const ProxyConfigurationDialog = ({
  firstLaunch,
  showProxyConfigurationDialog,
  setShowProxyConfigurationDialog,
  row,
}: ProxyConfigurationDialogProps) => {
  const [proxyCheckState, setProxyCheckState] = useState<
    'NONE' | 'CHECKING' | 'SUCCESS' | 'ERROR'
  >('NONE')
  const [proxyError, setProxyError] = useState<string | null>(null)
  const toast = useToast()
  const queryClient = useQueryClient()

  const { query, updateMutation } = useCreatorProxySettings(
    showProxyConfigurationDialog,
    row.original,
  )
  const { data } = query

  const currentSettings = data?.settings ?? {}
  const currentAutomaticProxyCountry =
    currentSettings.automaticProxy?.automaticProxyCountry

  // Putting in country even if there are no proxies available for it anymore
  const automaticProxyCountries = data?.automaticProxyCountries || {}

  if (
    currentAutomaticProxyCountry &&
    !automaticProxyCountries[currentAutomaticProxyCountry]
  ) {
    automaticProxyCountries[currentAutomaticProxyCountry] =
      currentAutomaticProxyCountry
  }

  const configFormValues: ProxyConfigurationFormValues = useMemo(
    () => ({
      proxyType: data?.settings.proxyType || 'NONE',
      host:
        data?.settings.host.substring(0, data?.settings.host.indexOf(':')) ||
        '',
      port:
        (data?.settings.host.includes(':') &&
          data?.settings.host.split(':')[1]) ||
        '',
      username: data?.settings.username || '',
      password: data?.settings.password || '',
      automaticProxyCountry:
        data?.settings.automaticProxy?.automaticProxyCountry || null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, row],
  )

  const form = useForm<ProxyConfigurationFormValues>({
    mode: 'onChange',
    defaultValues: configFormValues,
    resolver: zodResolver(ProxyConfigurationFormSchema),
  })

  const { handleSubmit, control } = form
  const [proxyType, host, username, port, password, automaticProxyCountry] =
    useWatch({
      name: [
        'proxyType',
        'host',
        'username',
        'port',
        'password',
        'automaticProxyCountry',
      ],
      control: control,
    })
  const isValidHttpProxy =
    proxyType === 'HTTP' &&
    host.length > 0 &&
    port.length > 0 &&
    username.length > 0 &&
    password.length > 0

  const closeDialog = () => {
    setShowProxyConfigurationDialog(false)
  }

  // Setting form to retrieved values when we got them
  useEffect(
    () => {
      form.reset(configFormValues)
      if (firstLaunch && data) {
        form.setValue(
          'proxyType',
          Object.keys(automaticProxyCountries).length > 0
            ? BrowserProfileProxyType.AUTOMATIC
            : BrowserProfileProxyType.HTTP,
          { shouldDirty: true },
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configFormValues],
  )

  // Initial proxy check state on mount
  useEffect(() => {
    setProxyCheckState('NONE')
  }, [])

  // Resetting proxy check success if input gets changed
  useEffect(
    () => {
      if (proxyCheckState === 'SUCCESS') {
        setProxyCheckState('NONE')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [host, port, username, password],
  )

  const renderField = (
    label: string,
    name: string,
    placeholder: string,
    type = 'text',
  ) => (
    <div className="flex flex-col gap-1.5">
      <span className="text-title text-sm font-medium">{label}</span>
      <Controller
        // @ts-ignore
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            type={type}
            // @ts-ignore
            value={field.value}
            onChange={field.onChange}
            placeholder={placeholder}
          />
        )}
      />
    </div>
  )

  return (
    <Dialog
      show={showProxyConfigurationDialog}
      onClose={closeDialog}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit((values) =>
            onSubmit(
              row.original,
              updateMutation,
              toast,
              queryClient,
              firstLaunch,
              proxyType,
              proxyCheckState,
              setProxyCheckState,
              setProxyError,
              setShowProxyConfigurationDialog,
              values,
            ),
          )}
        >
          <Dialog.Header>
            <Dialog.Title>
              {row.original.name}&#39;s{' '}
              {firstLaunch ? 'Launch Configuration' : 'Proxy Settings'}
            </Dialog.Title>
            <Dialog.Description>
              {firstLaunch && (
                <h1 className="text-title mb-1 mt-3 text-lg">Proxy settings</h1>
              )}
              We recommend to use a proxy to prevent OnlyFans from enforcing
              face verification for every login. CreatorHero provides you with a
              free & reliable static residential proxy for each of your
              creators.
            </Dialog.Description>
          </Dialog.Header>
          {!data && (
            <Dialog.Description>
              <Skeleton className="mt-3 h-80 w-full" />
            </Dialog.Description>
          )}
          {data && (
            <>
              <Dialog.Description>
                <div className="my-6 flex flex-col gap-6">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-title text-sm font-medium">
                      Proxy Type
                    </span>
                    <Controller
                      name="proxyType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={(value) => {
                            // @ts-ignore
                            field.onChange(value)
                          }}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {availableProxyTypes
                                .filter(
                                  (type) =>
                                    type !== 'AUTOMATIC' ||
                                    Object.keys(automaticProxyCountries)
                                      .length > 0,
                                )
                                .map((proxyType) => (
                                  <SelectItem
                                    key={proxyType}
                                    value={proxyType}
                                  >
                                    {proxyType
                                      .replace('NONE', 'None')
                                      .replace('AUTOMATIC', 'Automatic')}
                                  </SelectItem>
                                ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {proxyType === 'AUTOMATIC' && (
                    <div className="flex flex-col gap-1.5">
                      <span className="text-title text-sm font-medium">
                        Proxy Country
                      </span>
                      <Controller
                        name="automaticProxyCountry"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              disabled={currentAutomaticProxyCountry != null}
                              onValueChange={(value) => {
                                // @ts-ignore
                                field.onChange(value)
                              }}
                              // @ts-ignore
                              value={field.value}
                            >
                              <SelectTrigger>
                                {automaticProxyCountry != null && (
                                  <SelectValue />
                                )}
                                {automaticProxyCountry == null && (
                                  <div className="flex">
                                    Select a proxy country
                                  </div>
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {Object.entries(automaticProxyCountries).map(
                                    ([key, value]) => (
                                      <SelectItem
                                        key={key}
                                        value={key}
                                      >
                                        {String(value)}
                                      </SelectItem>
                                    ),
                                  )}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                            {currentAutomaticProxyCountry == null && (
                              <p className="text-warning-default text-xs">
                                The proxy country cannot be changed after you
                                chose it once.
                              </p>
                            )}
                            {currentAutomaticProxyCountry != null && (
                              <p className="text-warning-default text-xs">
                                If you have a problem with this proxy, contact
                                support.
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>
                  )}

                  {proxyType === 'HTTP' && (
                    <>
                      {renderField('Host', 'host', '0.0.0.0')}
                      {renderField('Port', 'port', '80', 'number')}
                      {renderField('Username', 'username', 'admin')}
                      {renderField('Password', 'password', 'admin123')}
                    </>
                  )}
                </div>
              </Dialog.Description>
              {proxyType === 'HTTP' && proxyCheckState === 'ERROR' && (
                <>
                  <p className="text-danger-default text-xs">
                    Proxy connection failed:
                  </p>
                  <pre className="text-title max-h-72 overflow-y-scroll rounded-md bg-gray-700/20 p-2 text-xs dark:bg-gradient-to-b dark:from-gray-700/40 dark:to-gray-700/20">
                    <code>{proxyError!.replaceAll('\\n', '\n')}</code>
                  </pre>
                </>
              )}
              {proxyType === 'HTTP' && proxyCheckState === 'SUCCESS' && (
                <p className="text-success-default text-xs">
                  Successfully connected to proxy!
                </p>
              )}
              <Dialog.Footer>
                {proxyType === 'HTTP' && proxyCheckState !== 'SUCCESS' && (
                  <Button
                    type="submit"
                    disabled={!isValidHttpProxy}
                    isLoading={proxyCheckState === 'CHECKING'}
                    className="w-full"
                  >
                    Test Connection
                  </Button>
                )}
                {(proxyType !== 'HTTP' || proxyCheckState === 'SUCCESS') && (
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      proxyType === 'AUTOMATIC' && automaticProxyCountry == null
                    }
                    isLoading={updateMutation.isLoading}
                  >
                    {firstLaunch && !updateMutation.isLoading && (
                      <OfIcon className="mr-[4px]" />
                    )}
                    {firstLaunch ? 'Save & Start OnlyFans' : 'Save'}
                  </Button>
                )}
              </Dialog.Footer>
            </>
          )}
        </form>
      </FormProvider>
    </Dialog>
  )
}
